/**
 * Attach the session token to every browser fetch aimed at the Archer API (#2045).
 *
 * The backend now authenticates every /api route. Most calls go through
 * apiClient, which already sends the token, but some components still call
 * fetch() directly. Installing this once at startup gives those calls the same
 * Authorization header instead of letting them 401.
 *
 * Scope is deliberately narrow: only requests to the Archer API (same-origin
 * /api/, or /api/ under the configured API base URL) are touched,
 * and an Authorization header the caller already set is never replaced.
 * Requests to any other origin never receive the token.
 */
import { resolveApiBaseUrl } from '../config/apiBaseUrl'

const TOKEN_STORAGE_KEY = 'ignis_auth_token'
const INSTALLED = Symbol.for('archer.apiFetchAuth.installed')

export function isArcherApiUrl(url: string, apiBaseUrl: string = resolveApiBaseUrl(), pageUrl: string = window.location.href): boolean {
  let target: URL
  let page: URL
  try {
    target = new URL(url, pageUrl)
    page = new URL(pageUrl)
  } catch {
    return false
  }
  // Same-origin /api/ (served or proxied by the frontend host).
  if (target.origin === page.origin && target.pathname.startsWith('/api/')) return true
  if (!apiBaseUrl) return false
  // The configured API base URL.
  let base: URL
  try {
    base = new URL(apiBaseUrl, pageUrl)
  } catch {
    return false
  }
  if (target.origin !== base.origin) return false
  const basePath = base.pathname.replace(/\/+$/, '')
  return target.pathname.startsWith(`${basePath}/api/`)
}

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

export function installApiFetchAuth(target: typeof globalThis = globalThis): void {
  const holder = target as typeof globalThis & { [INSTALLED]?: boolean }
  if (holder[INSTALLED] || typeof target.fetch !== 'function') return
  holder[INSTALLED] = true

  const originalFetch = target.fetch.bind(target)
  target.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const token = readToken()
    if (!token || !isArcherApiUrl(requestUrl(input))) return originalFetch(input, init)

    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
    if (headers.has('Authorization')) return originalFetch(input, init)
    headers.set('Authorization', `Bearer ${token}`)
    return originalFetch(input, { ...init, headers })
  }
}
